import { useEffect, useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ComposeMailDialog } from "@/features/mail/ComposeMailDialog";
import { holdComposeDraft, isComposeDraftKey, releaseComposeDraft } from "@/features/mail/composeDrafts";
import { rememberComposeWindowPlace } from "@/features/mail/composeWindow";
import {
  listMailboxes,
  getMailAccountStatus,
  isPersonalMailbox,
  mailKeys,
  personalMailboxFrom,
  type ReplyExpected,
  type SharedMailbox,
} from "@/features/mail/mail.api";
import { fetchSession, sessionQueryKey, hasModule } from "@/lib/auth";
import { Skeleton } from "@/components/feedback/Skeleton";

// A new mail in a window of its own, to drag to the other screen (Camiel,
// 2026-10-14). Outside the _shell layout for the reason mail-window.tsx gives:
// a window with the menu bars is a second copy of the application, not a mail.
//
// The window opens on a draft key and reads the draft the main window wrote
// there; see composeWindow.ts. While it is open it marks that draft as held, so
// a Nieuwe mail in the main window does not pick the same one up.

const HOLD_EVERY_MS = 20_000;

export const Route = createFileRoute("/mail-compose")({
  // The same guard as mail-window: the session cookie travels with a second
  // window, and the module check is the component's, not a redirect.
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData({
      queryKey: sessionQueryKey,
      queryFn: fetchSession,
    });
    if (!session) throw redirect({ to: "/login", search: { redirect: location.href } });
    return { session };
  },
  validateSearch: (search: Record<string, unknown>) => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    const files = Number(search["files"]);
    return {
      mailbox: str(search["mailbox"]),
      draft: str(search["draft"]),
      files: Number.isInteger(files) && files > 0 ? files : undefined,
    };
  },
  component: ComposeWindow,
});

// Closing a window the script did not open (the address typed by hand) does
// nothing; the page then says it is done instead of looking stuck.
function closeWindow() {
  rememberComposeWindowPlace();
  window.close();
}

function ComposeWindow() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const { session } = Route.useRouteContext();
  const mailboxId = search.mailbox ?? "";
  const draftKey = search.draft ?? "";
  const personalWanted = isPersonalMailbox(mailboxId);
  const validDraft = Boolean(mailboxId) && isComposeDraftKey(mailboxId, draftKey);
  // Sent: what Verwacht reactie left behind, or that there was nothing to say.
  const [sent, setSent] = useState<{ replyExpected?: ReplyExpected } | null>(null);
  const [closed, setClosed] = useState(false);

  const canMail = hasModule(session, "mail") || hasModule(session, "mail_personal");

  const mailboxesQuery = useQuery({
    queryKey: mailKeys.mailboxes,
    queryFn: () => listMailboxes(),
    enabled: canMail && validDraft && !personalWanted,
  });
  const personalQuery = useQuery({
    queryKey: mailKeys.account,
    queryFn: getMailAccountStatus,
    enabled: canMail && validDraft && personalWanted,
  });
  // Only a mailbox you are a member of, as on the mail screen: sending as one
  // you never joined would only come back refused, after the whole mail.
  const mailbox: SharedMailbox | null = personalWanted
    ? personalMailboxFrom(personalQuery.data, t("mail.myMailbox"), { ready: true })
    : (mailboxesQuery.data?.find(
        (m) => m.id === mailboxId && m.members.some((mem) => mem.id === session.userId),
      ) ?? null);

  // Held while open, renewed, and let go when the window goes; where it stood
  // is kept for the next one.
  useEffect(() => {
    if (!validDraft || sent || closed) return;
    holdComposeDraft(draftKey);
    const timer = window.setInterval(() => holdComposeDraft(draftKey), HOLD_EVERY_MS);
    const leave = () => {
      rememberComposeWindowPlace();
      releaseComposeDraft(draftKey);
    };
    window.addEventListener("pagehide", leave);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("pagehide", leave);
      releaseComposeDraft(draftKey);
    };
  }, [validDraft, draftKey, sent, closed]);

  useEffect(() => {
    document.title = t("mail.composeTitle");
  }, [t]);

  if (!canMail) return <WindowNote text={t("mail.windowNoAccess")} />;
  if (!validDraft) return <WindowNote text={t("mail.composeWindowMissing")} />;
  if (sent) return <SentNote replyExpected={sent.replyExpected} onClose={closeWindow} />;
  if (closed) return <WindowNote text={t("mail.composeWindowClosed")} />;

  const loading = personalWanted ? personalQuery.isLoading : mailboxesQuery.isLoading;
  if (loading) {
    return (
      <div style={{ padding: "24px" }}>
        <Skeleton height="18px" width="40%" />
        <div style={{ marginTop: "16px" }}>
          <Skeleton height="240px" />
        </div>
      </div>
    );
  }
  if (!mailbox) return <WindowNote text={t("mail.composeWindowNoMailbox")} />;

  return (
    <div data-screen-label="Back-office Mail Compose Window" style={{ height: "100vh", background: "var(--surface-base)" }}>
      <ComposeMailDialog
        mailbox={mailbox}
        draftKey={draftKey}
        slotId="window"
        inOwnWindow
        filesToReceive={search.files ?? 0}
        onSubjectChange={(subject) => {
          document.title = subject.trim() || t("mail.composeTitle");
        }}
        canAi={hasModule(session, "ai")}
        onClose={() => {
          setClosed(true);
          closeWindow();
        }}
        onSent={(replyExpected) => {
          setSent({ replyExpected });
          // Nothing to tell: the window goes at once.
          if (!replyExpected) closeWindow();
        }}
      />
    </div>
  );
}

// After sending with Verwacht reactie: which task now waits, and the window
// shuts itself as the toast in the main window disappears.
function SentNote({ replyExpected, onClose }: { replyExpected?: ReplyExpected; onClose: () => void }) {
  const { t } = useTranslation();
  const [seconds, setSeconds] = useState(10);

  useEffect(() => {
    if (!replyExpected) return;
    if (seconds <= 0) {
      onClose();
      return;
    }
    const timer = window.setTimeout(() => setSeconds((v) => v - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [seconds, replyExpected, onClose]);

  const text = !replyExpected
    ? t("mail.composeWindowSent")
    : "failed" in replyExpected
      ? t("mail.expectReplyFailed")
      : t(replyExpected.created ? "mail.expectReplyCreated" : "mail.expectReplyExisting", {
          number: replyExpected.number ?? "",
        });

  return (
    <div
      data-screen-label="Back-office Mail Compose Window"
      className="wl-vstack"
      style={{ gap: "14px", padding: "48px 32px", alignItems: "center", textAlign: "center" }}
    >
      <p className="t-body">{text}</p>
      <div className="wl-hstack" style={{ gap: "8px" }}>
        {/* A tab, not this window: a link here would turn the little window
            into the whole application. */}
        {replyExpected && "taskId" in replyExpected && (
          <a
            className="wl-btn wl-btn--secondary"
            href={`/tasks/${encodeURIComponent(replyExpected.taskId)}`}
            target="_blank"
            rel="noopener"
          >
            {t("mail.expectReplyOpenTask")}
          </a>
        )}
        <button className="wl-btn wl-btn--ghost" onClick={onClose}>
          {replyExpected ? t("mail.windowClose", { seconds }) : t("common.close")}
        </button>
      </div>
    </div>
  );
}

function WindowNote({ text }: { text: string }) {
  return (
    <div data-screen-label="Back-office Mail Compose Window" style={{ padding: "32px", textAlign: "center" }}>
      <p className="t-body-sm t-muted">{text}</p>
    </div>
  );
}
